import React, { useState } from 'react';
import { ArrowLeft, BookOpen, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { createTextPost } from '../../api/posts';
import { useCategories } from '../../api/categories';
import MonetizationPicker, { MIN_TEASER } from './MonetizationPicker';

const MAX_BODY = 50000;

/**
 * RaccontoTextForm — Phase F6. Titolo + testo puro (nessun editor complesso,
 * nessun HTML). Reuses the posts economics via POST /api/posts/text.
 */
const RaccontoTextForm = ({ onBack }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { categories } = useCategories();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [categorySlug, setCategorySlug] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  const [price, setPrice] = useState('100');
  const [teaser, setTeaser] = useState('');
  const [saving, setSaving] = useState(false);

  const publish = async () => {
    if (!title.trim()) {
      toast({ title: 'Titolo mancante', description: 'Dai un titolo al tuo Racconto.', variant: 'destructive' });
      return;
    }
    if (!categorySlug) {
      toast({ title: 'Categoria mancante', description: 'Scegli una categoria per il tuo Racconto.', variant: 'destructive' });
      return;
    }
    if (!body.trim()) {
      toast({ title: 'Testo mancante', description: 'Scrivi il testo del Racconto prima di pubblicare.', variant: 'destructive' });
      return;
    }
    if (isPaid && teaser.trim().length < MIN_TEASER) {
      toast({ title: 'Anteprima troppo corta', description: `L'anteprima pubblica richiede almeno ${MIN_TEASER} caratteri.`, variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await createTextPost({
        title: title.trim(),
        body_text: body.trim(),
        category_slug: categorySlug,
        is_paid: isPaid,
        price: isPaid ? parseInt(price, 10) : 0,
        preview_description: isPaid ? teaser.trim() : '',
      });
      toast({ title: 'Racconto pubblicato', description: 'Il tuo Racconto scritto è ora nella tua Vetrina.' });
      navigate(`/u/${user.username}`);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      toast({
        title: 'Pubblicazione non riuscita',
        description: (typeof detail === 'string' && detail.split('|').pop()) || err.message || 'Riprova.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-[720px] px-4 pt-6 pb-24" data-testid="racconto-text-form">
      <button
        onClick={onBack}
        className="mb-3 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        data-testid="racconto-text-back-btn"
      >
        <ArrowLeft className="h-4 w-4" /> Indietro
      </button>
      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="flex items-center gap-2 text-xl font-bold">
          <BookOpen className="h-5 w-5 gold-text" /> Racconto Testuale
        </h2>

        <div className="mt-5 space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold">Titolo *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 150))}
              placeholder="Es. Quando persi il treno a Oslo"
              disabled={saving}
              data-testid="racconto-text-title"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Categoria *</label>
            <select
              value={categorySlug}
              onChange={(e) => setCategorySlug(e.target.value)}
              disabled={saving}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              data-testid="racconto-text-category"
            >
              <option value="" disabled>Seleziona una categoria</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-muted-foreground">Scegli tu la categoria: il tipo di contenuto non la determina.</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Testo del Racconto *</label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
              placeholder="Scrivi qui il tuo racconto…"
              className="min-h-[260px] resize-y text-[15px] leading-relaxed"
              maxLength={MAX_BODY}
              disabled={saving}
              data-testid="racconto-text-body"
            />
            <div className="mt-1 text-right text-[11px] tabular-nums text-muted-foreground" data-testid="racconto-text-counter">
              {body.length.toLocaleString()} / {MAX_BODY.toLocaleString()}
            </div>
          </div>

          <MonetizationPicker
            scope="post_text"
            isPaid={isPaid}
            setIsPaid={setIsPaid}
            price={price}
            setPrice={setPrice}
            teaser={teaser}
            setTeaser={setTeaser}
            disabled={saving}
          />

          <button
            onClick={publish}
            disabled={saving || !title.trim() || !body.trim() || !categorySlug}
            className="flex w-full items-center justify-center gap-2 rounded-full gold-gradient px-5 py-3 text-sm font-bold text-black disabled:opacity-50"
            data-testid="racconto-text-publish-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {saving ? 'Pubblicazione…' : 'Pubblica Racconto'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RaccontoTextForm;
